import type { UserRole } from '@statura/types';
import { SAH_MACHINERY_COHORTS, type ClientFundingCohort } from './client-funding-context';

/**
 * Professional-scope contract for governed clinical assessments.
 *
 * `captureRoles` may record observations or administer the instrument where
 * trained and within local policy. `signOffRoles` own the clinical
 * interpretation and follow-through. An EN capture that requires RN
 * delegation must include a named RN delegate before save/sign-off.
 *
 * This is deliberately template-code based. Assessment-type-only policy is too
 * coarse: PAIN contains both a person-rated NRS and an observational form;
 * NUTRITION contains a clinical review and a permission-gated named tool.
 */
export interface AssessmentScopePolicy {
  templateCode: string;
  clinicalOwner: 'RN' | 'RN_OR_ALLIED_HEALTH' | 'CARE_MANAGEMENT_WITH_RN_REVIEW';
  captureRoles: readonly UserRole[];
  signOffRoles: readonly UserRole[];
  rnDelegationRequiredFor: readonly UserRole[];
  rationale: string;
}

const RN: readonly UserRole[] = ['REGISTERED_NURSE'];
const RN_OR_EN: readonly UserRole[] = ['REGISTERED_NURSE', 'ENROLLED_NURSE'];
const RN_OR_AH: readonly UserRole[] = ['REGISTERED_NURSE', 'ALLIED_HEALTH'];
const CLINICAL_CAPTURE: readonly UserRole[] = [
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
];
const CARE_MANAGEMENT_CAPTURE: readonly UserRole[] = [
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'COORDINATOR',
  'CASE_MANAGER',
  'TEAM_LEADER',
];
const CARE_MANAGEMENT_SIGNOFF: readonly UserRole[] = ['REGISTERED_NURSE'];
const CARE_MANAGEMENT_REQUIRING_RN: readonly UserRole[] = [
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'COORDINATOR',
  'CASE_MANAGER',
  'TEAM_LEADER',
];
const NON_CLINICAL_CAPTURE_REQUIRING_RN: readonly UserRole[] = [
  'ENROLLED_NURSE',
  'COORDINATOR',
  'CASE_MANAGER',
  'TEAM_LEADER',
];

function policy(
  templateCode: string,
  input: Omit<AssessmentScopePolicy, 'templateCode'>,
): AssessmentScopePolicy {
  return { templateCode, ...input };
}

const RN_OWNED_REASON =
  'Clinical interpretation and follow-through are RN-owned; EN capture requires named RN delegation.';

export const ASSESSMENT_SCOPE_POLICIES: Readonly<Record<string, AssessmentScopePolicy>> =
  Object.fromEntries(
    [
      ...[
        'platform_delirium_4at',
        'platform_pressure_injury_risk_screen',
        'platform_continence_structured',
        'platform_dysphagia_eat10',
        'platform_hydration',
        'platform_medication_review',
        'platform_palliative_akps',
        'platform_palliative_sas',
        'platform_wound_time',
      ].map((templateCode) =>
        policy(templateCode, {
          clinicalOwner: 'RN',
          captureRoles: RN_OR_EN,
          signOffRoles: RN,
          rnDelegationRequiredFor: ['ENROLLED_NURSE'],
          rationale: RN_OWNED_REASON,
        }),
      ),
      policy('platform_abbey_pain_scale', {
        clinicalOwner: 'RN',
        captureRoles: RN_OR_EN,
        signOffRoles: RN,
        rnDelegationRequiredFor: ['ENROLLED_NURSE'],
        rationale:
          'Observational pain assessment in a person unable to verbalise requires RN-owned interpretation, intervention and reassessment.',
      }),
      policy('platform_pain_nrs', {
        clinicalOwner: 'RN',
        captureRoles: CARE_MANAGEMENT_CAPTURE,
        signOffRoles: RN,
        rnDelegationRequiredFor: CARE_MANAGEMENT_REQUIRING_RN,
        rationale:
          'The person or a trained worker may record the rating; a nurse owns clinical interpretation and severe-pain follow-through.',
      }),
      policy('platform_depression_gds15', {
        clinicalOwner: 'RN',
        captureRoles: CARE_MANAGEMENT_CAPTURE,
        signOffRoles: RN,
        rnDelegationRequiredFor: CARE_MANAGEMENT_REQUIRING_RN,
        rationale:
          'The screen may be administered by a trained worker, but a positive or unresolved result requires RN-owned mental-health and self-harm review.',
      }),
      policy('platform_mobility_tug', {
        clinicalOwner: 'RN_OR_ALLIED_HEALTH',
        captureRoles: CLINICAL_CAPTURE,
        signOffRoles: RN_OR_AH,
        rnDelegationRequiredFor: ['ENROLLED_NURSE'],
        rationale:
          'TUG is a supervised performance test. RN or appropriately qualified allied health owns interpretation; EN capture requires RN delegation.',
      }),
      policy('platform_reablement_gas', {
        clinicalOwner: 'RN_OR_ALLIED_HEALTH',
        captureRoles: RN_OR_AH,
        signOffRoles: RN_OR_AH,
        rnDelegationRequiredFor: [],
        rationale:
          'Reablement goal definition and outcome review are owned by the treating RN or allied-health professional in partnership with the person.',
      }),
      ...[
        'platform_adl_index',
        'platform_iadl_checklist',
        'platform_carer_strain_checklist',
        'platform_falls_risk_screen',
        'platform_home_safety_environment_assessment',
        'platform_manual_handling',
        'platform_nutrition_mst',
        'platform_nutrition_risk_screen',
        'platform_oral_health_screen',
        'platform_sensory_screen',
        'platform_sleep',
        'platform_social_isolation_lsns6',
      ].map((templateCode) =>
        policy(templateCode, {
          clinicalOwner: 'RN_OR_ALLIED_HEALTH',
          captureRoles: CARE_MANAGEMENT_CAPTURE,
          signOffRoles: RN_OR_AH,
          rnDelegationRequiredFor: NON_CLINICAL_CAPTURE_REQUIRING_RN,
          rationale:
            'Trained staff may capture observations; RN or the relevant allied-health professional owns clinical interpretation and follow-through.',
        }),
      ),
      ...[
        'platform_advance_care_planning',
        'platform_behaviour_support_plan',
        'platform_behaviour_symptoms_screen',
        'platform_cognition_screen',
        'platform_discharge_transition_review',
        'platform_periodic_client_review',
        'platform_sah_onboarding_assessment',
      ].map((templateCode) =>
        policy(templateCode, {
          clinicalOwner: 'CARE_MANAGEMENT_WITH_RN_REVIEW',
          captureRoles: CARE_MANAGEMENT_CAPTURE,
          signOffRoles: CARE_MANAGEMENT_SIGNOFF,
          rnDelegationRequiredFor: CARE_MANAGEMENT_REQUIRING_RN,
          rationale:
            'Care management may capture the overall record; its clinical sections and any high-risk finding require RN review before final sign-off.',
        }),
      ),
    ].map((item) => [item.templateCode, item]),
  );

export type AssessmentScopeAction = 'CAPTURE' | 'SIGN_OFF';

export interface AssessmentScopeDecision {
  allowed: boolean;
  requiresNamedRnDelegate: boolean;
  policy: AssessmentScopePolicy | null;
  reason: 'allowed' | 'role_not_in_scope' | 'rn_delegate_required' | 'unclassified_template';
}

/**
 * Unknown/provider-authored templates remain governed by their existing tenant
 * workflow until classified. Platform templates must never silently fall back
 * to that permissive path.
 */
export function assessmentScopeDecision(input: {
  templateCode: string | null | undefined;
  actorRole: UserRole;
  action: AssessmentScopeAction;
  namedRnDelegateId?: string | null;
}): AssessmentScopeDecision {
  const templateCode = input.templateCode?.trim() ?? '';
  const scope = ASSESSMENT_SCOPE_POLICIES[templateCode] ?? null;
  if (!scope) {
    return {
      allowed: !templateCode.startsWith('platform_'),
      requiresNamedRnDelegate: false,
      policy: null,
      reason: 'unclassified_template',
    };
  }

  const allowedRoles = input.action === 'SIGN_OFF' ? scope.signOffRoles : scope.captureRoles;
  if (!allowedRoles.includes(input.actorRole)) {
    return {
      allowed: false,
      requiresNamedRnDelegate: false,
      policy: scope,
      reason: 'role_not_in_scope',
    };
  }

  const needsDelegate =
    input.action === 'CAPTURE' && scope.rnDelegationRequiredFor.includes(input.actorRole);
  if (needsDelegate && !input.namedRnDelegateId) {
    return {
      allowed: false,
      requiresNamedRnDelegate: true,
      policy: scope,
      reason: 'rn_delegate_required',
    };
  }

  return {
    allowed: true,
    requiresNamedRnDelegate: needsDelegate,
    policy: scope,
    reason: 'allowed',
  };
}

export function unclassifiedPlatformAssessmentCodes(codes: readonly string[]): string[] {
  return codes.filter((code) => code.startsWith('platform_') && !ASSESSMENT_SCOPE_POLICIES[code]);
}

/** Shared web/mobile mirror of the server program-scope guard. */
export function assessmentProgramScopeMatchesFundingPrograms(input: {
  programScope: readonly string[] | null | undefined;
  fundingPrograms: readonly unknown[] | null | undefined;
  fundingCohort?: ClientFundingCohort | string | null;
}): boolean {
  const scopes = new Set(
    (input.programScope ?? []).map((value) => String(value).trim().toUpperCase()),
  );
  const programs = new Set(
    (input.fundingPrograms ?? []).map((value) => {
      const raw =
        value && typeof value === 'object'
          ? ((value as Record<string, unknown>).type ??
            (value as Record<string, unknown>).value ??
            (value as Record<string, unknown>).program ??
            (value as Record<string, unknown>).label ??
            '')
          : value;
      return String(raw ?? '')
        .trim()
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toUpperCase()
        .replace(/[\s-]+/g, '_');
    }),
  );
  if (scopes.size === 0 || scopes.has('ALL') || scopes.has('BOTH')) return true;
  const fundingCohort = String(input.fundingCohort ?? '').toUpperCase() as ClientFundingCohort;
  const usesSahMachinery = SAH_MACHINERY_COHORTS.has(fundingCohort);
  if (
    [...scopes].some((scope) => ['SAH', 'SUPPORT_AT_HOME', 'HOME_CARE'].includes(scope)) &&
    (usesSahMachinery ||
      [...programs].some((program) =>
        ['SUPPORT_AT_HOME', 'SUPPORTATHOME', 'SAH'].includes(program),
      ))
  ) {
    return true;
  }
  if (
    [...scopes].some((scope) => ['RAC', 'RESIDENTIAL', 'AN_ACC'].includes(scope)) &&
    [...programs].some((program) =>
      [
        'AN_ACC',
        'RAC',
        'RAC_PERMANENT',
        'RAC_RESPITE',
        'RESIDENTIAL',
        'RESIDENTIAL_CARE',
        'RESIDENTIAL_AGED_CARE',
        'RESIDENTIAL_PERMANENT',
        'RESIDENTIAL_RESPITE',
        'PERMANENT',
        'RESPITE',
        'AGED_CARE',
        'ANACC',
      ].includes(program),
    )
  ) {
    return true;
  }
  return [...scopes].some((scope) => programs.has(scope));
}

export type AssessmentQuestionProgramScope = 'SAH' | 'RAC' | 'PROGRAM_NEUTRAL' | 'UNRESOLVED';

function authoredProgramScopes(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const row = value as Record<string, unknown>;
  const metadata =
    row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
      ? (row.metadata as Record<string, unknown>)
      : null;
  const raw = row.programScopes ?? row.program_scopes ?? metadata?.programScopes;
  return Array.isArray(raw)
    ? raw.map((item) => String(item).trim().toUpperCase()).filter(Boolean)
    : [];
}

function programScopedItemApplies(value: unknown, targetProgram: AssessmentQuestionProgramScope) {
  const scopes = authoredProgramScopes(value);
  if (scopes.length === 0 || scopes.includes('ALL') || scopes.includes('PROGRAM_NEUTRAL')) {
    return true;
  }
  if (targetProgram === 'UNRESOLVED' || targetProgram === 'PROGRAM_NEUTRAL') return false;
  return scopes.includes(targetProgram);
}

function questionIdentity(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const row = value as Record<string, unknown>;
  return String(row.key ?? row.id ?? '').trim();
}

function pruneAssessmentTruthContract(value: unknown, visibleQuestionIds: Set<string>) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const contract = { ...(value as Record<string, unknown>) };
  const filterQuestionIds = (items: unknown) =>
    Array.isArray(items)
      ? items.map(String).filter((questionId) => visibleQuestionIds.has(questionId))
      : [];
  const filterRules = (items: unknown, keys: string[]) =>
    Array.isArray(items)
      ? items
          .filter((rule) => {
            if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return false;
            const row = rule as Record<string, unknown>;
            const directKeysVisible = keys.every((key) => {
              const questionId = String(row[key] ?? '');
              return !questionId || visibleQuestionIds.has(questionId);
            });
            const when = row.when;
            const whenQuestionId =
              when && typeof when === 'object' && !Array.isArray(when)
                ? String((when as Record<string, unknown>).questionId ?? '')
                : '';
            return directKeysVisible && (!whenQuestionId || visibleQuestionIds.has(whenQuestionId));
          })
          .map((rule) => {
            const row = { ...(rule as Record<string, unknown>) };
            if ('questionIds' in row) row.questionIds = filterQuestionIds(row.questionIds);
            if ('detailQuestionIds' in row)
              row.detailQuestionIds = filterQuestionIds(row.detailQuestionIds);
            return row;
          })
      : items;

  contract.conditionalRequired = Array.isArray(contract.conditionalRequired)
    ? contract.conditionalRequired
        .filter((rule) => {
          if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return false;
          const when = (rule as Record<string, unknown>).when;
          const sourceQuestionId =
            when && typeof when === 'object' && !Array.isArray(when)
              ? String((when as Record<string, unknown>).questionId ?? '')
              : '';
          return !sourceQuestionId || visibleQuestionIds.has(sourceQuestionId);
        })
        .map((rule) => ({
          ...(rule as Record<string, unknown>),
          questionIds: filterQuestionIds((rule as Record<string, unknown>).questionIds),
        }))
        .filter((rule) => (rule.questionIds as string[]).length > 0)
    : contract.conditionalRequired;
  contract.detailStateRules = filterRules(contract.detailStateRules, ['stateQuestionId']);
  contract.contradictions = filterRules(contract.contradictions, ['questionId']);
  contract.numericStateRules = filterRules(contract.numericStateRules, [
    'questionId',
    'targetQuestionId',
  ]);
  contract.rawOverrides = filterRules(contract.rawOverrides, ['questionId']);
  if (contract.outcomeQuestionId && !visibleQuestionIds.has(String(contract.outcomeQuestionId))) {
    delete contract.outcomeQuestionId;
    delete contract.outcomes;
    delete contract.rawOverrides;
  }
  if (
    contract.exclusiveChoiceValues &&
    typeof contract.exclusiveChoiceValues === 'object' &&
    !Array.isArray(contract.exclusiveChoiceValues)
  ) {
    contract.exclusiveChoiceValues = Object.fromEntries(
      Object.entries(contract.exclusiveChoiceValues as Record<string, unknown>).filter(([key]) =>
        visibleQuestionIds.has(key),
      ),
    );
  }
  return contract;
}

/**
 * Removes program-specific assessment sections/questions before rendering,
 * scoring or client-side completion validation. Unresolved context deliberately
 * exposes only unscoped/program-neutral content; the database remains the
 * final authority and separately rejects cross-program responses.
 */
export function assessmentQuestionSchemaForProgram<T extends Record<string, unknown>>(
  questionSchema: T | null | undefined,
  targetProgram: AssessmentQuestionProgramScope,
): T | null | undefined {
  if (!questionSchema) return questionSchema;
  const rawSections = Array.isArray(questionSchema.sections) ? questionSchema.sections : [];
  const sections = rawSections
    .filter((section) => programScopedItemApplies(section, targetProgram))
    .map((section) => {
      const row = { ...(section as Record<string, unknown>) };
      const questions = Array.isArray(row.questions) ? row.questions : [];
      row.questions = questions.filter((question) =>
        programScopedItemApplies(question, targetProgram),
      );
      return row;
    })
    .filter((section) => (section.questions as unknown[]).length > 0);
  const visibleQuestionIds = new Set(
    sections.flatMap((section) =>
      (section.questions as unknown[]).map(questionIdentity).filter(Boolean),
    ),
  );
  const filtered = { ...questionSchema, sections } as Record<string, unknown>;
  for (const key of ['truthContract', 'completionTruthContract'] as const) {
    if (filtered[key]) {
      filtered[key] = pruneAssessmentTruthContract(filtered[key], visibleQuestionIds);
    }
  }
  return filtered as T;
}
